import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { MapPin, Star } from "lucide-react";

export default function PublicProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/public/profiles/${userId}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [userId]);

  if (error) return <div className="p-16 text-center text-[#68706B]">This profile does not exist or is not public.</div>;
  if (!data) return <div className="p-16 text-center text-[#68706B]">Loading…</div>;

  const p = data.user;
  const avatarSrc = p.avatar?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${p.avatar}` : p.avatar;
  const primary = (p.professions || []).find((x) => x.priority === "primary");
  const secondary = (p.professions || []).filter((x) => x.priority !== "primary");

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="public-profile">
      <div className="bg-white border border-[#E2E4E2] p-8 flex flex-col sm:flex-row gap-6 items-start">
        <div className="w-24 h-24 bg-[#E8ECEB] overflow-hidden flex items-center justify-center text-3xl font-bold text-[#1E3F33] shrink-0">
          {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : p.name?.[0]?.toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight" data-testid="public-name">{p.name}</h1>
            {p.is_verified && <VerifiedBadge className="w-5 h-5" />}
          </div>
          {p.location && <div className="text-sm text-[#68706B] mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{p.location}</div>}
          <div className="flex flex-wrap gap-2 mt-3">
            {primary && (
              <span className="px-3 py-1 text-xs font-semibold bg-[#1E3F33] text-white inline-flex items-center gap-1">
                <Star className="w-3 h-3 fill-[#F2C94C] text-[#F2C94C]" />{primary.name}
              </span>
            )}
            {secondary.map((s) => (
              <span key={s.name} className="px-3 py-1 text-xs bg-[#F0EFEB] text-[#1A1E1C]">{s.name}</span>
            ))}
          </div>
          {p.bio && <p className="text-sm text-[#1A1E1C] mt-3 max-w-xl">{p.bio}</p>}
          {!user && (
            <Link to="/register?role=hr">
              <Button className="rounded-none bg-[#D96C5B] hover:bg-[#B85948] mt-4" data-testid="public-cta">Sign up to contact {p.name?.split(" ")[0]}</Button>
            </Link>
          )}
        </div>
      </div>

      <h2 className="text-lg font-bold tracking-tight mt-8">Visual resumes</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4" data-testid="public-videos">
        {data.videos.length === 0 && <div className="text-sm text-[#68706B] col-span-full">No approved videos yet.</div>}
        {data.videos.map((v) => (
          <div key={v.id} className="relative aspect-[3/4] bg-black overflow-hidden">
            <video src={fileUrl(v.video_file_id)} poster={v.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${v.thumbnail_url}` : undefined} controls playsInline className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/80 text-white text-xs font-semibold pointer-events-none">{v.title}</div>
          </div>
        ))}
      </div>

      {!user && (
        <div className="mt-10 bg-[#1E3F33] text-white p-8 text-center">
          <div className="text-xl font-bold tracking-tight">Hiring? Find more candidates like {p.name?.split(" ")[0]} on VisualCV.</div>
          <Link to="/register"><Button className="rounded-none bg-white text-[#1E3F33] hover:bg-[#E8ECEB] mt-4">Get started free</Button></Link>
        </div>
      )}
    </div>
  );
}
